import React, {
    forwardRef,
    MutableRefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef
} from 'react';
import {LexicalEditor} from 'lexical';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import {$generateHtmlFromNodes} from '@lexical/html';

import {
    ToolbarPlugin,
    RichTextPlugin,
    DragDropPaste,
    HistoryPlugin,
    HtmlPlugin,
    TreeViewPlugin,
    LinkPlugin,
    FocusPlugin,
    AutoFocusPlugin,
    ImagesPlugin,
    ListMaxIndentLevelPlugin,
    AutoLinkPlugin,
    HorizontalRulePlugin,
    ListPlugin,
    TablePlugin,
    ContentEditable
} from './plugins';
import {Placeholder} from './ui/Placeholder';
import {getEditorConfig} from './editorConfig';
import {DirectionPlugin} from './plugins/DirectionPlugin';
import {ExposeEditorInstancePlugin} from './plugins/ExposeEditorInstancePlugin';
import {EmojisPlugin} from './plugins/EmojisPlugin';
import './editor-theme.scss';

type Props = {
    onSave: () => void;
    value: string;
    placeholder?: string;
    isReadonly?: boolean;
    onFocus?: () => void;
    autofocus?: boolean;
    namespace: string;
    onUnmount?: (html: string) => void;
};

export type ExposedEditorData = {
    editor: MutableRefObject<LexicalEditor>;
};

const params = new URLSearchParams(window.location.search);
const debugHtml = Number(params.get('_lexical-editor-debug'));

export const Editor = forwardRef((props: Props, ref) => {
    const {
        onFocus,
        onSave,
        value = '',
        placeholder,
        isReadonly,
        namespace,
        onUnmount,
        autofocus
    } = props;
    const editorConfig = useRef(getEditorConfig(namespace));
    const editorRef = useRef<LexicalEditor>();
    const editorDivRef = useRef<HTMLDivElement>(null);
    const onUnmountCallbackRef = useRef(onUnmount);

    const setEditorInstance = useCallback(editor => {
        editorRef.current = editor;
    }, []);

    useImperativeHandle(
        ref,
        (): ExposedEditorData => ({
            editor: editorRef
        }),
        []
    );

    useEffect(() => {
        const editorInstance = editorRef.current;

        return function onUnmount() {
            editorInstance?.update(() => {
                const html = $generateHtmlFromNodes(editorInstance);
                onUnmountCallbackRef.current?.(html);
            });
        };
    }, []);
    requestAnimationFrame(() => {
        !isReadonly && editorDivRef?.current.focus();
    });

    return (
        <LexicalComposer initialConfig={editorConfig.current}>
            <div className="priority-editor">
                <ToolbarPlugin onSave={onSave} />

                <div className="priority-editor__inner">
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                ref={editorDivRef}
                                contentEditable={!isReadonly}
                                className="priority-editor__content"
                            />
                        }
                        placeholder={<Placeholder text={placeholder} />}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <ExposeEditorInstancePlugin callback={setEditorInstance} />
                    <DragDropPaste />
                    <HistoryPlugin />
                    <TablePlugin cellEditorConfig={editorConfig.current} />
                    <ListPlugin />
                    <LinkPlugin />
                    <ImagesPlugin />
                    <HorizontalRulePlugin />
                    <AutoLinkPlugin />
                    <DirectionPlugin />
                    <ListMaxIndentLevelPlugin maxDepth={7} />
                    <EmojisPlugin />

                    {autofocus && <AutoFocusPlugin defaultSelection={'rootStart'} />}
                    <FocusPlugin onFocus={onFocus} />

                    <HtmlPlugin initialHtml={value} />

                    {debugHtml ? <TreeViewPlugin /> : null}
                </div>
            </div>
        </LexicalComposer>
    );
});
